import React, { useState, useContext, useEffect, useRef } from 'react';
import { TableContext } from '../../context';
import '../../App.css';
import { FaTimes } from 'react-icons/fa'; // Import the "X" icon from react-icons
import { toast } from "react-toastify";
import axios from 'axios';
import { McTypeaheadMultiSelect } from "@maersk-global/mds-react-wrapper/components-core/mc-typeahead-multi-select";
import { McTag } from "@maersk-global/mds-react-wrapper/components-core/mc-tag";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";

/**
 * Debounce function to delay the execution of a function.
 * @param {Function} func - The function to debounce.
 * @param {number} wait - The delay in milliseconds.
 */
// const debounce = (func, wait) => {
//   let timeout;
//   return (...args) => {
//     clearTimeout(timeout);
//     timeout = setTimeout(() => func(...args), wait);
//   };
// };

/**
 * Search component for searching application names.
 * Provides suggestions based on user input and allows fetching table data.
 */
function SearchTest({ setSearch }) {
  // State for application search (typeahead multi-select)
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [applicationSuggestions, setApplicationSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [isFreeTextTaggingEnabled, setIsFreeTextTaggingEnabled] = useState(false);
  
  // Ref for the typeahead component
  const typeaheadRef = useRef(null);
  
  // Ref for the tags container to enable auto-scroll
  const tagsContainerRef = useRef(null);
  
  const {getToken} = useContext(TableContext)

  // Auto-scroll to the latest tag when new applications are added
  useEffect(() => {
    if (tagsContainerRef.current && selectedApplications.length > 0) {
      // Small delay to ensure the DOM has updated
      setTimeout(() => {
        tagsContainerRef.current.scrollTop = tagsContainerRef.current.scrollHeight;
      }, 100);
    }
  }, [selectedApplications.length]); // Only trigger when the count changes

  // Set up event listeners for the typeahead component
  useEffect(() => {
    const typeaheadElement = typeaheadRef.current;
    
    if (typeaheadElement) {
      // Add event listeners directly to the element
      const handleOptionSelected = (event) => {
        handleApplicationSelect(event);
        
        // Also check the component's selecteddata property after a short delay
        setTimeout(() => {
          if (typeaheadElement.selecteddata) {
            setSelectedApplications(typeaheadElement.selecteddata);
          }
        }, 100);
      };
      
      const handleSearch = (event) => {
        handleApplicationSearch(event);
      };
      
      // Also listen for any changes to selecteddata
      const handleSelectionChange = () => {
        if (typeaheadElement.selecteddata) {
          setSelectedApplications(typeaheadElement.selecteddata);
        }
      };
      
      typeaheadElement.addEventListener('optionselected', handleOptionSelected);
      typeaheadElement.addEventListener('search', handleSearch);
      
      // Set up a mutation observer to watch for changes to selecteddata
      const observer = new MutationObserver(() => {
        handleSelectionChange();
      });
      
      observer.observe(typeaheadElement, {
        attributes: true,
        attributeFilter: ['selecteddata'],
        childList: true,
        subtree: true
      });
      
      // Cleanup
      return () => {
        typeaheadElement.removeEventListener('optionselected', handleOptionSelected);
        typeaheadElement.removeEventListener('search', handleSearch);
        observer.disconnect();
      };
    }
  }, []); // Run once on mount

  /**
   * Handles typeahead multi-select search for applications
   */
  const handleApplicationSearch = async (e) => {
    const searchValue = e.target.value;
    
    // Check if input starts with CHG (case insensitive) to enable free text tagging
    const startsWithCHG = searchValue.toLowerCase().startsWith('chg');
    setIsFreeTextTaggingEnabled(startsWithCHG);
    
    // Skip suggestions if input starts with CHG (case insensitive)
    if (startsWithCHG) {
      return;
    }
    
    if (searchValue.length >= 3) {
      // Set loading state on the component directly
      if (typeaheadRef.current) {
        typeaheadRef.current.loading = true;
      }
      setIsLoadingSuggestions(true);
      
      try {
        const url = /^[Aa]\d{2,}$/.test(searchValue)
          ? `${process.env.REACT_APP_BACKEND_URI}/api/change_data/getAppIDSuggestions`
          : `${process.env.REACT_APP_BACKEND_URI}/api/change_data/getAppNameSuggestions`;

        const response = await axios.post(
          url, 
          { app_name: searchValue },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: "Bearer " + localStorage.getItem("jwt_token"),
            }
          }
        );

        if (response.data.status === 200) {
          const suggestions = response.data.data || [];
          const formattedSuggestions = suggestions.map(item => ({ 
            label: item, 
            value: item,
            id: item 
          }));
          
          setApplicationSuggestions(formattedSuggestions);
          
          // Set data directly on component
          if (typeaheadRef.current) {
            typeaheadRef.current.data = [...formattedSuggestions];
          }
        } else {
          setApplicationSuggestions([]);
          if (typeaheadRef.current) {
            typeaheadRef.current.data = [];
          }
        }
      } catch (error) {
        console.error('Error fetching application suggestions:', error);
        setApplicationSuggestions([]);
        if (typeaheadRef.current) {
          typeaheadRef.current.data = [];
        }
      } finally {
        setIsLoadingSuggestions(false);
        if (typeaheadRef.current) {
          typeaheadRef.current.loading = false;
        }
      }
    }
  };

  /**
   * Handles application selection from typeahead multi-select
   */
  const handleApplicationSelect = (e) => { 
    // Clear the data after selection
    setApplicationSuggestions([]);
    if (typeaheadRef.current) {
      typeaheadRef.current.data = [];
      typeaheadRef.current.loading = false;
    }
    
    // Update selected applications based on the event detail structure
    if (e.detail) {
      // Check all possible properties in the event detail
      if (e.detail.selecteddata) {
        setSelectedApplications(e.detail.selecteddata);
      } else if (e.detail.selectedData) {
        setSelectedApplications(e.detail.selectedData);
      } else if (e.detail.value) {
        // Handle single selection or array of values
        const currentSelections = selectedApplications || [];
        
        if (Array.isArray(e.detail.value)) {
          setSelectedApplications(e.detail.value);
        } else {
          // Add to existing selections if it's not already there
          const exists = currentSelections.some(item => 
            (typeof item === 'string' ? item : item.value) === 
            (typeof e.detail.value === 'string' ? e.detail.value : e.detail.value.value)
          );
          
          if (!exists) {
            const newItem = typeof e.detail.value === 'string' 
              ? { label: e.detail.value, value: e.detail.value, id: e.detail.value }
              : e.detail.value;
            setSelectedApplications([...currentSelections, newItem]);
          }
        }
      } else if (e.detail.option) {
        // Handle option property
        const currentSelections = selectedApplications || [];
        const newItem = e.detail.option;
        
        const exists = currentSelections.some(item => 
          (typeof item === 'string' ? item : item.value) === 
          (typeof newItem === 'string' ? newItem : newItem.value)
        );
        
        if (!exists) {
          setSelectedApplications([...currentSelections, newItem]);
        }
      } else {
        // Try to get selected data directly from the component
        if (typeaheadRef.current && typeaheadRef.current.selecteddata) {
          setSelectedApplications(typeaheadRef.current.selecteddata);
        }
      }
    }
  };

  /**
   * Handles typeahead clear button click
   */
  const handleTypeaheadClearClick = (e) => {
    // Clear all suggestions and data immediately
    setApplicationSuggestions([]);
    setIsFreeTextTaggingEnabled(false); // Reset free text tagging
    
    if (typeaheadRef.current) {
      typeaheadRef.current.data = [];
      typeaheadRef.current.loading = false;
      
      // Clear input value
      if (typeaheadRef.current.value !== undefined) {
        typeaheadRef.current.value = '';
      }
      
      // Clear the actual input element
      const inputElement = typeaheadRef.current.querySelector('input');
      if (inputElement) {
        inputElement.value = '';
      }
    }
  };

  /**
   * Removes an application from the selected list
   */
  const removeApplication = (index) => {
    const newSelectedApplications = selectedApplications.filter((_, i) => i !== index);
    setSelectedApplications(newSelectedApplications);
    
    // Also remove from the typeahead component if it has a removeSelectedOption method
    if (typeaheadRef.current && typeaheadRef.current.removeSelectedOption) {
      const appToRemove = selectedApplications[index];
      typeaheadRef.current.removeSelectedOption(appToRemove);
    }
    
    // Clear search if no applications left (similar to Search.jsx behavior)
    if (newSelectedApplications.length === 0) {
      setSearch('');
    }
  };

  /**
   * Clears all selected applications
   */
  const clearAllApplications = () => {
    setSelectedApplications([]);
    setApplicationSuggestions([]);
    setIsFreeTextTaggingEnabled(false); // Reset free text tagging
    setSearch(''); // Clear search to reset table (similar to Search.jsx behavior)
    
    // Clear the component data and selected data directly
    if (typeaheadRef.current) {
      typeaheadRef.current.data = [];
      // Clear selected data if the property exists
      if (typeaheadRef.current.selecteddata !== undefined) {
        typeaheadRef.current.selecteddata = [];
      }
    }
  };

  /**
   * Handles the click event of the search button.
   * Pass the selected applications to search after resolving App IDs to names.
   */
  const handleButtonClick = async () => {
    if (selectedApplications.length === 0) {
      toast.error("Please select at least one application or change", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      return;
    }
    
    // Extract application values from selected applications
    const apps = selectedApplications.map(app => typeof app === 'string' ? app : app.value);
    const resolvedAppNames = [];
    
    for (const app of apps) {
      if (/^[Aa]\d{2,}$/.test(app)) {
        const appNames = await fetchAppNames(app);
        resolvedAppNames.push(...appNames);
      } else {
        resolvedAppNames.push(app);
      }
    }
    
    const cleanedInput = resolvedAppNames.join(', ');
    setSearch(cleanedInput);
  };

  const fetchAppNames = async (appId) => {
    try {
        const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_URI}/api/change_data/getAppNameFromAppId`,
            { app_id: appId },
            {
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                    'Content-Type': 'application/json'
                }
            }
        );
        
        if (response.status === 200) {
            return response.data.data;
        } else {
            console.error('Failed to fetch app names for App ID:', appId);
            return [];
        }
    } catch (error) {
        console.error('Error fetching app names for App ID:', appId, error);
        return [];
    }
  };

  return (
    <div className="search-container">
      <div className="search-bar" style={{ display: "flex", flexDirection: "row", gap: "16px", alignItems: "flex-start" }}>
        
        {/* Search Input Container */}
        <div style={{ flex: 1, maxWidth: "400px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <McTypeaheadMultiSelect
              ref={typeaheadRef}
              hiddenlabel={true}
              hiddentags={true}
              placeholder="Search for Applications or Changes"
              name="applications"
              minchars="3"
              disablefilter={true}
              clearbutton={true}
              clearbuttonclick={handleTypeaheadClearClick}
              nosuggestions={isFreeTextTaggingEnabled ? "Press Enter to add Change Number" : "No suggestions available"}
              optionsheight={"200px"}
              freetexttagging={isFreeTextTaggingEnabled}
              style={{
                width: "100%"
              }}
              data={applicationSuggestions}
            />
            {/* Selected Applications Tags Container */}
            {selectedApplications.length > 0 && (
              <div 
                ref={tagsContainerRef}
                style={{
                  maxHeight: "30px",
                  overflowY: "auto",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "6px",
                  scrollBehavior: "smooth",
                  alignItems: "center",
                }}
              >
                {selectedApplications.map((app, index) => (
                  <McTag
                    key={index}
                    appearance="neutral-weak"
                    dismiss={(e) => removeApplication(index)}
                    withaction
                  >
                    {typeof app === 'string' ? app : app.value || app.label}
                  </McTag>
                ))}
                {/* Clear all button - show when 2 or more items selected */}
                {selectedApplications.length >= 2 && (
                  <button
                    type="button"
                    onClick={clearAllApplications}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#0077b2",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "500",
                      padding: "4px 8px",
                      whiteSpace: "nowrap",
                    }}
                    onMouseOver={(e) => e.target.style.color = "#0077b2"}
                    onMouseOut={(e) => e.target.style.color = "#0077b2"}
                  >
                    Clear all
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Search Button */}
        <div style={{ display: "flex", alignItems: "flex-start" }}>
          <McButton 
            click={handleButtonClick} 
            appearance="primary"
          >
            Search
          </McButton>
        </div>
      </div>
    </div>
  );
}

export default SearchTest;